/**
 * V13 confirmed direct-damage / cadence / boss calibration baseline.
 *
 * This module is deliberately a deterministic calibration surface, not a
 * runtime player-classification system and not a second damage authority.
 * It expresses the confirmed reference formulas in natural units (basic-hit
 * multiples, basic-attack cycles, and H_ref) so solver/reference work can
 * consume one versioned source instead of duplicating spreadsheet constants.
 */

import { NonFiniteNumericError } from '../math.js';
import type { ReferenceProgressionBand } from '../reference/types.js';
import type { SkillGrowthProfile } from '../types.js';
import { PROVISIONAL_BURST_CAP_PROFILE } from '../burst/profile.js';

export const V13_SKILL_DAMAGE_CALIBRATION_VERSION = 'v13-confirmed-2026-08-22' as const;

/**
 * MASTERY.1: `SkillGrowthProfile`'s type-level vocabulary owner moved to
 * src/types.ts (single-owner requirement); re-exported here for backward
 * compatibility with existing importers of this module.
 */
export type { SkillGrowthProfile } from '../types.js';
export { SKILL_GROWTH_PROFILES } from '../types.js';

/** Independent BalanceProfile coordinate; never derive this from item multiplier 1/2/4. */
export const V13_REFERENCE_GROWTH_X: Readonly<Record<ReferenceProgressionBand, number>> = Object.freeze({
  novice: 0.7407,
  intermediate: 1.0,
  advanced: 1.4074,
});

export const V13_PROFILE_GAMMA = 0.75;
/** Current/Growth F/K-style profile separation: 0.90/0.10 vs 0.10/0.90. */
export const V13_PROFILE_SPECIALIZATION = 0.90;

export const V13_MASTERY_MAX_LEVEL = 10;
export const V13_MASTERY_INITIAL_PENALTY = 0.70;
export const V13_MASTERY_ETA = 1.4415057553718058;

function requireFinitePositive(value: number, name: string, allowZero = false): void {
  if (!Number.isFinite(value)) throw new NonFiniteNumericError(`${name} must be finite ${allowZero ? '>= 0' : '> 0'} (got ${value})`);
  if (allowZero ? value < 0 : value <= 0) {
    throw new Error(`${name} must be finite ${allowZero ? '>= 0' : '> 0'} (got ${value})`);
  }
}

export function masteryRealization(level: number): number {
  if (!Number.isInteger(level) || level < 1 || level > V13_MASTERY_MAX_LEVEL) {
    throw new Error(`mastery level must be an integer in [1, ${V13_MASTERY_MAX_LEVEL}] (got ${level})`);
  }
  return 1 - V13_MASTERY_INITIAL_PENALTY * Math.pow(1 - (level - 1) / 9, V13_MASTERY_ETA);
}

function growthSignal(x: number): number {
  requireFinitePositive(x, 'growth coordinate x');
  return Math.pow(x, V13_PROFILE_GAMMA);
}

/**
 * Stage 2 (plan `sparkling-purring-allen`) — the three named profiles are
 * samples of one continuous axis `t ∈ [0, 1]`, not three independent
 * formulas: `current = 0`, `normal = 0.5`, `growth = 1`. Internal to this
 * module and `fk-authoring.ts`'s F/K weight split — not yet exposed to
 * authoring (schema exposure of `t` itself is a separate decision).
 */
export const V13_PROFILE_T: Readonly<Record<SkillGrowthProfile, number>> = Object.freeze({
  current: 0,
  normal: 0.5,
  growth: 1,
});

/**
 * F/K-style weight split at axis position `t`, linear between the ratified
 * endpoints: `wF(0) = a` (current, F-heavy), `wF(1) = 1 − a` (growth,
 * K-heavy), `wF(0.5) = 0.5` (normal — the two weights cross exactly at
 * mid-axis regardless of `a`). Verified bit-identical to the old three-value
 * `switch` at `t = 0 / 0.5 / 1` (`test/calibration-v13-continuous-t.test.ts`).
 */
export function wF(t: number): number {
  const a = V13_PROFILE_SPECIALIZATION;
  return a - (2 * a - 1) * t;
}

export function wK(t: number): number {
  return 1 - wF(t);
}

/**
 * Stage 4 (plan sparkling-purring-allen) — `hStat/hBase` for the
 * standardized Lv60 WEAPON reference snapshot (`standardizedReferenceSource('WEAPON', 60)`
 * resolved through `resolveSourceProfile`). This module is a deterministic
 * calibration surface with no runtime/reference-layer imports (`reference/
 * weapon-progression-v0.ts` itself imports FROM here, so importing it back
 * would cycle) — a literal, pinned bit-exact against the real production
 * computation by `test/calibration-v13-continuous-t.test.ts`, mirroring
 * `V13_REFERENCE_GROWTH_X`'s own "never derive this" precedent above.
 * `phi`'s burst-cap denominator, below.
 */
export const V13_R_REF = 3.42;

/**
 * Stage 4 (plan sparkling-purring-allen) burst-cap crafter-relative
 * multiplier. `r = hStat/hBase` — the same stat-share axis `wF`/`wK` are
 * already defined over (Stage 2). `phi(t, r_crafter) / phi(t, V13_R_REF)`
 * is the ratio a rarity's flat burst cap is scaled by, so the SAME skill
 * (same `t`, same declared q) reads as legal/illegal identically no matter
 * which crafter it was authored on — see `burst/assess.ts`.
 */
export function phi(t: number, r: number): number {
  requireFinitePositive(r, 'r (hStat/hBase)', true);
  return (wF(t) + wK(t) * r) / (1 + r);
}

export function rawGrowthProfile(profile: SkillGrowthProfile, x: number): number {
  const gx = growthSignal(x);
  const t = V13_PROFILE_T[profile];
  return wF(t) + wK(t) * gx;
}

/** Relative modifier only: ordinary level/item/stat progression is already owned by H/H_ref. */
export function growthProfileMultiplierAtX(profile: SkillGrowthProfile, x: number): number {
  return rawGrowthProfile(profile, x) / rawGrowthProfile('normal', x);
}

export function growthProfileMultiplier(profile: SkillGrowthProfile, band: ReferenceProgressionBand): number {
  return growthProfileMultiplierAtX(profile, V13_REFERENCE_GROWTH_X[band]);
}

export const V13_HIGH_REPEAT = Object.freeze({
  normalFullHitMultiple: 3.0,
  actionCycles: 0.80,
  recastCycles: 2.50,
});

export const V13_MID_REPEAT = Object.freeze({
  actionCycles: 1.00,
  recastCycles: 6.25,
});

export function highRepeatFullHitMultipleAtX(profile: SkillGrowthProfile, x: number): number {
  return V13_HIGH_REPEAT.normalFullHitMultiple * growthProfileMultiplierAtX(profile, x);
}

export function highRepeatFullHitMultiple(profile: SkillGrowthProfile, band: ReferenceProgressionBand): number {
  return highRepeatFullHitMultipleAtX(profile, V13_REFERENCE_GROWTH_X[band]);
}

/** Incremental sustained output relative to source BasicDPS at full Mastery. */
function highRepeatIncrementalBasicDpsRatioAtX(profile: SkillGrowthProfile, x: number): number {
  return (highRepeatFullHitMultipleAtX(profile, x) - V13_HIGH_REPEAT.actionCycles) / V13_HIGH_REPEAT.recastCycles;
}

export function highRepeatIncrementalBasicDpsRatio(profile: SkillGrowthProfile, band: ReferenceProgressionBand): number {
  return highRepeatIncrementalBasicDpsRatioAtX(profile, V13_REFERENCE_GROWTH_X[band]);
}

/**
 * Mid-repeat spends cadence on per-hit concentration but keeps high-repeat's
 * M10 incremental BasicDPS. Continuous-`x` generalization of the existing
 * band-restricted formula (SOL.3A-0 remediation) — no new coefficient, same
 * derivation, just evaluated at any `x` instead of only the three anchor
 * bands. Affine in `growthProfileMultiplierAtX(profile, x)`, never
 * proportional — a caller must not approximate it as `nominal * S`.
 */
export function midRepeatFullHitMultipleAtX(profile: SkillGrowthProfile, x: number): number {
  return V13_MID_REPEAT.actionCycles
    + highRepeatIncrementalBasicDpsRatioAtX(profile, x) * V13_MID_REPEAT.recastCycles;
}

export function midRepeatFullHitMultiple(profile: SkillGrowthProfile, band: ReferenceProgressionBand): number {
  return midRepeatFullHitMultipleAtX(profile, V13_REFERENCE_GROWTH_X[band]);
}

export function runtimeHitMultiple(fullMasteryHitMultiple: number, masteryLevel: number): number {
  requireFinitePositive(fullMasteryHitMultiple, 'fullMasteryHitMultiple', true);
  return fullMasteryHitMultiple * masteryRealization(masteryLevel);
}

export type LowRepeatAvailabilityKind = 'ordinary_cooldown' | 'rolling_charge';

export interface LowRepeatAvailabilityPolicy {
  readonly kind: LowRepeatAvailabilityKind;
  /** Common minimum interval abstraction used by solver/evaluator. */
  readonly minimumAvailabilityIntervalS: number;
  /** Present only for the macro rolling-charge policy. */
  readonly capacity?: 1;
  /** Present only for the macro rolling-charge policy. */
  readonly rollingRechargeS?: number;
}

export interface LowRepeatAnchor {
  readonly id: 'low' | 'very_high' | 'maximum_reference';
  /** Normal-profile nominal M10 damage in H_ref. */
  readonly nominalQRef: number;
  readonly minimumAvailabilityIntervalS: number;
  readonly availabilityKind: LowRepeatAvailabilityKind;
}

/** Profile never changes these availability anchors. */
export const V13_LOW_REPEAT_ANCHORS: readonly LowRepeatAnchor[] = Object.freeze([
  { id: 'low', nominalQRef: 35, minimumAvailabilityIntervalS: 60, availabilityKind: 'ordinary_cooldown' },
  { id: 'very_high', nominalQRef: 70, minimumAvailabilityIntervalS: 240, availabilityKind: 'ordinary_cooldown' },
  { id: 'maximum_reference', nominalQRef: 200, minimumAvailabilityIntervalS: 86_400, availabilityKind: 'rolling_charge' },
]);

/**
 * Pure-damage concentration envelope in nominal Normal-profile H_ref units.
 * A longer interval is a minimum requirement, never an automatic damage purchase.
 */
export function minimumAvailabilityIntervalS(nominalQRef: number): number {
  requireFinitePositive(nominalQRef, 'nominalQRef');
  if (nominalQRef < 35) {
    throw new Error(`low-repeat concentration envelope starts at 35 H_ref (got ${nominalQRef})`);
  }
  if (nominalQRef <= 70) {
    return 60 * Math.exp((Math.log(4) / 35) * (nominalQRef - 35));
  }
  return 240 * Math.exp((Math.log(360) / 130) * (nominalQRef - 70));
}

/**
 * 35H/70H remain ordinary cooldowns. At 200H+ the same minimum-interval
 * abstraction is enforced by a capacity-1 rolling charge, avoiding midnight reset exploits.
 */
export function resolveLowRepeatAvailability(nominalQRef: number): LowRepeatAvailabilityPolicy {
  const minimum = minimumAvailabilityIntervalS(nominalQRef);
  if (nominalQRef < 200) {
    return { kind: 'ordinary_cooldown', minimumAvailabilityIntervalS: minimum };
  }
  return {
    kind: 'rolling_charge',
    minimumAvailabilityIntervalS: minimum,
    capacity: 1,
    rollingRechargeS: minimum,
  };
}

/** Nominal 200H is the Normal-profile maximum reference, not the hard cap itself. */
export const V13_MAX_REFERENCE_NORMAL_Q_REF = 200;
/**
 * Top-rarity M10 per-activation seed; leaves headroom for the confirmed
 * Growth profile. R5's absolute peak across the whole crafter-anchor surface
 * (R5's `activationBurst` in `PROVISIONAL_BURST_CAP_PROFILE`, 250H) x
 * phi(1, r_advanced)/phi(1, V13_R_REF) (the `t=1`/advanced-crafter
 * peak of `phi`, see below); this is the smallest whole H that carries a 2%
 * margin over it. Computed, not a literal: `r` is `slope x Q_D` and Q_D of the
 * canonical build is linear in level, so the Lv100 (advanced) `r` is
 * `V13_R_REF x 100/60`; `test/v13-skill-damage-calibration.test.ts` checks this
 * against the production Lv100 snapshot. 277 at the 3% damage slope
 * (250 x 1.0857 = 271.4); it was 328 at 0.3% (250 x 1.2849 = 321.2).
 */
export const V13_TOP_B_PEAK_Q_REF = Math.ceil(
  PROVISIONAL_BURST_CAP_PROFILE.rules.find((rule) => rule.rarity === 'rarity5_PROVISIONAL')!.caps.activationBurst
  * (phi(1, V13_R_REF * (100 / 60)) / phi(1, V13_R_REF)) * 1.02,
);

export function maxReferenceFullQRef(profile: SkillGrowthProfile, band: ReferenceProgressionBand): number {
  return V13_MAX_REFERENCE_NORMAL_Q_REF * growthProfileMultiplier(profile, band);
}

export function isWithinTopPeak(fullQRef: number): boolean {
  requireFinitePositive(fullQRef, 'fullQRef', true);
  return fullQRef <= V13_TOP_B_PEAK_Q_REF + 1e-9;
}

/**
 * Boss EHP calibration is expressed in H_ref, independent of skill damage authority.
 *
 * This is the **single** authority for "how tough is a boss".
 * `reference/encounters.ts` derives `R1_BOOTSTRAP_ENCOUNTERS`' boss
 * `target.maxHp` from {@link bossEhpFixture} rather than holding its own
 * numbers: R.1 previously sized the same bosses in a flat 10-damage probe
 * unit, which put the two authorities 12x (novice) to 49x (advanced) apart.
 *
 * The curve's intended lower anchor is **75H at Lv10** — R.1's phase brief
 * §11/§12 "boss takes 50-100 player hits, center 75" describes the lowest
 * encounterable boss, not every boss. There is no Lv10 progression band yet
 * (bands are Lv20/60/100), so 75 is recorded here as the curve's origin
 * rather than as a fourth entry.
 */
export const V13_BOSS_EHP_Q_REF: Readonly<Record<ReferenceProgressionBand, number>> = Object.freeze({
  novice: 130,
  intermediate: 160,
  advanced: 190,
});

/**
 * BOOTSTRAP. Ordinary (non-boss) encounter EHP in H_ref multiples.
 *
 * R.1's phase brief pins ordinary enemies at 1-3 player hits, center 2
 * (`expectedPlayerHitsToKill`), so trash stays trash at every band while
 * {@link V13_BOSS_EHP_Q_REF} lets bosses grow relatively tankier (130 -> 190).
 * Introduced when `reference/encounters.ts` moved off the flat 10-damage unit
 * — the old 20/27/38 values were 2.0/2.7/3.8 of that fixture and had no
 * authority of their own. Not approved balance data; the "2 hits" intent is
 * what is load-bearing, not this number's precision.
 */
export const V13_NORMAL_EHP_Q_REF = 2;

/**
 * Current deterministic simulation fixture. These are not `x` and are not the
 * production progression law; they are the Balanced-Sword H_ref values used by V13 validation.
 */
export const V13_BALANCED_H_REF_DAMAGE: Readonly<Record<ReferenceProgressionBand, number>> = Object.freeze({
  novice: 134.392,
  intermediate: 555.152,
  advanced: 1683.04,
});

export const V13_EXPERIMENTAL_ITEM_WEAPON_POWER_MULTIPLIER: Readonly<Record<ReferenceProgressionBand, number>> = Object.freeze({
  novice: 1,
  intermediate: 2,
  advanced: 4,
});

export function bossEhpFixture(band: ReferenceProgressionBand): number {
  return V13_BOSS_EHP_Q_REF[band] * V13_BALANCED_H_REF_DAMAGE[band];
}

/** Ordinary-encounter counterpart of {@link bossEhpFixture}. */
export function normalEhpFixture(band: ReferenceProgressionBand): number {
  return V13_NORMAL_EHP_Q_REF * V13_BALANCED_H_REF_DAMAGE[band];
}

/** Raw EHP fraction only. Boss phases/gimmicks may still prevent one-activation encounter clear. */
export function sameTierBossRawDamageFraction(
  nominalQRef: number,
  profile: SkillGrowthProfile,
  band: ReferenceProgressionBand,
  masteryLevel: number,
): number {
  requireFinitePositive(nominalQRef, 'nominalQRef', true);
  const realizedQ = nominalQRef * growthProfileMultiplier(profile, band) * masteryRealization(masteryLevel);
  return realizedQ / V13_BOSS_EHP_Q_REF[band];
}
